import React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Tooltip } from "@mui/material";
import { CheckCircle, Error, Pending, Warning } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useAppSelector } from "../../../../app/hooks";
import { ReimbursementCalcRow, ReimbursementRowStatus } from "../../../../services/accountService/model/account/ReimbursementCalcSlice";

interface StatusCellProps {
    dataItem: ReimbursementCalcRow;
}

const StatusCell: React.FC<StatusCellProps> = ({ dataItem }) => {
    const getStatusDisplay = (status: ReimbursementRowStatus, errorMessage?: string) => {
        switch (status) {
            case 'pending':
                return (
                    <Tooltip title="Pending">
                        <Pending sx={{ color: '#9e9e9e' }} />
                    </Tooltip>
                );
            case 'calculating':
                return (
                    <Tooltip title="Calculating...">
                        <CircularProgress size={20} sx={{ color: '#2196f3' }} />
                    </Tooltip>
                );
            case 'success':
                return (
                    <Tooltip title="Success">
                        <CheckCircle sx={{ color: '#4caf50' }} />
                    </Tooltip>
                );
            case 'warning':
                return (
                    <Tooltip title={errorMessage || 'Completed with warning'}>
                        <Warning sx={{ color: '#ff9800' }} />
                    </Tooltip>
                );
            case 'error':
                return (
                    <Tooltip title={errorMessage || 'Calculation failed'}>
                        <Error sx={{ color: '#f44336' }} />
                    </Tooltip>
                );
            default:
                return null;
        }
    };

    return (
        <td style={{ textAlign: 'center' }}>
            {getStatusDisplay(dataItem.status, dataItem.errorMessage)}
        </td>
    );
};

const ReimbursementCell: React.FC<{ dataItem: ReimbursementCalcRow }> = ({ dataItem }) => {
    return (
        <td style={{ textAlign: 'right' }}>
            {dataItem.totalReimbursement !== undefined 
                ? `$${dataItem.totalReimbursement.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : '-'}
        </td>
    );
};

const ReimbursementCalcGrid: React.FC = () => {
    const { rows } = useAppSelector(state => state.reimbursementCalc);

    // Status sort order: errors first, then warnings, then calculating, then pending, then success
    const statusSortOrder: Record<ReimbursementRowStatus, number> = {
        'error': 0,
        'warning': 1,
        'calculating': 2,
        'pending': 3,
        'success': 4
    };

    const sortedRows = [...rows].sort((a, b) => {
        return statusSortOrder[a.status] - statusSortOrder[b.status];
    });

    return (
        <Grid
            data={sortedRows}
            style={{ height: '400px' }}
            sortable={false}
            filterable={false}
        >
            <Column
                field="status"
                title="Status"
                width="80px"
                cell={(props) => <StatusCell dataItem={props.dataItem} />}
            />
            <Column
                field="accountNumber"
                title="Account Number"
                width="150px"
            />
            <Column
                field="totalReimbursement"
                title="Calculated Reimbursement"
                width="180px"
                cell={(props) => <ReimbursementCell dataItem={props.dataItem} />}
            />
            <Column
                field="errorMessage"
                title="Message"
            />
        </Grid>
    );
};

export { ReimbursementCalcGrid };
